/**
 * Active project context.
 *
 * The Requirement Lifecycle (SC-U15) and Requirement Doctor (SC-U16) screens
 * stay top-level screens with their own routes — they are not nested under
 * `/projects/:projectId`. They therefore need to be told which project they are
 * operating on, and that is what this provider supplies.
 *
 * Resolution order for the active project:
 *   1. `?projectId=` in the URL   (deep links from the project page)
 *   2. sessionStorage             (survives navigation within the tab)
 *   3. the only project, if the user has exactly one
 *   4. nothing — the screens render a "pick a project" state
 *
 * The URL is the single source of truth whenever it carries an id, and state
 * only ever flows *towards* it. An earlier version synced both ways — an effect
 * copying the URL into state and another copying state into the URL — which
 * oscillated forever the moment the two disagreed (selecting a project after
 * arriving on a deep link). Keep the flow one-directional.
 *
 * Whatever the source, the id is only accepted if it appears in the list
 * returned by `GET /projects`, which the backend scopes to the actor's
 * organization. That check keeps a hand-edited `?projectId=` from pointing a
 * screen at a project the user cannot see; the backend refuses such requests
 * as well, since the requirement endpoints check the project role
 * (`openfga.enforce_requirements`).
 *
 * The list is fetched when the app starts and again each time a requirement
 * screen is entered, so a project created, deleted or re-shared elsewhere in
 * the app shows up without a page reload. The re-fetch keeps the current list
 * on screen until the answer arrives.
 *
 * sessionStorage rather than localStorage is deliberate: a project that
 * outlives the browser session invites picking up yesterday's context without
 * noticing, and per-tab storage lets two projects be open side by side.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
  listAccessibleProjects,
  type AccessibleProject,
} from '../../api/accessibleProjects';

export const ACTIVE_PROJECT_STORAGE_KEY = 'ses-active-project-id';
export const ACTIVE_PROJECT_QUERY_PARAM = 'projectId';

/**
 * Routes that actually operate on a project. The selection is reflected into
 * the URL only here — the provider wraps the whole app, and stamping
 * `?projectId=` onto `/catalog` or `/projects/5` would both litter unrelated
 * routes and produce copied links whose path and query disagree.
 */
const PROJECT_SCOPED_PATHS = ['/requirements', '/requirement-doctor'];

function isProjectScopedPath(pathname: string): boolean {
  return PROJECT_SCOPED_PATHS.some(
    prefix => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

/**
 * What a consuming screen should render. `ready` is the only state in which
 * `activeProjectId` is a usable number.
 */
export type ActiveProjectStatus =
  | 'loading'
  | 'error'
  | 'empty'
  | 'unselected'
  | 'denied'
  | 'ready';

export interface ActiveProjectContextValue {
  status: ActiveProjectStatus;
  /** Non-null only when `status === 'ready'`. */
  activeProjectId: number | null;
  activeProject: AccessibleProject | null;
  projects: AccessibleProject[];
  /** Pass null to clear the selection. Ignores ids the user cannot access. */
  selectProject: (projectId: number | null) => void;
  /** Re-fetch the project list (e.g. after creating a project). */
  reload: () => void;
}

const ActiveProjectContext = createContext<ActiveProjectContextValue | null>(
  null,
);

function parseProjectId(raw: string | null): number | null {
  if (!raw) return null;
  const parsed = Number(raw);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
}

function readStoredProjectId(): number | null {
  if (typeof window === 'undefined') return null;
  try {
    return parseProjectId(
      window.sessionStorage.getItem(ACTIVE_PROJECT_STORAGE_KEY),
    );
  } catch {
    // Private mode / blocked storage — treat as "nothing remembered".
    return null;
  }
}

function writeStoredProjectId(projectId: number | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (projectId === null) {
      window.sessionStorage.removeItem(ACTIVE_PROJECT_STORAGE_KEY);
    } else {
      window.sessionStorage.setItem(
        ACTIVE_PROJECT_STORAGE_KEY,
        String(projectId),
      );
    }
  } catch {
    // Non-fatal: the selection simply will not survive a reload.
  }
}

export const ActiveProjectProvider = ({ children }: PropsWithChildren<{}>) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { pathname } = useLocation();
  const [projects, setProjects] = useState<AccessibleProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  // Used only while the URL carries no id of its own.
  const [fallbackId, setFallbackId] = useState<number | null>(
    readStoredProjectId,
  );
  // Auto-selecting a lone project happens once, on the first resolved list
  // that has a project — never again, so it cannot undo a deliberate
  // deselection. An empty list does not count: a user's first project is
  // created after the app has loaded, and picked up by the re-fetch below.
  const autoSelectedRef = useRef(false);

  const urlProjectId = parseProjectId(
    searchParams.get(ACTIVE_PROJECT_QUERY_PARAM),
  );
  const requestedId = urlProjectId ?? fallbackId;

  // Only the latest fetch may write: an older answer arriving late would
  // otherwise put back a list the user has already moved past.
  const fetchGenRef = useRef(0);

  const load = useCallback(async () => {
    const gen = ++fetchGenRef.current;
    setLoading(true);
    setFailed(false);
    try {
      const next = await listAccessibleProjects();
      if (gen === fetchGenRef.current) setProjects(next);
    } catch {
      if (gen === fetchGenRef.current) {
        setProjects([]);
        setFailed(true);
      }
    } finally {
      if (gen === fetchGenRef.current) setLoading(false);
    }
  }, []);

  // Re-fetch without going back to `loading`, which would unmount the screen.
  // A failure keeps the list already shown: it was valid a moment ago, and the
  // backend still refuses anything the user is no longer allowed to do.
  const refresh = useCallback(async () => {
    const gen = ++fetchGenRef.current;
    try {
      const next = await listAccessibleProjects();
      if (gen === fetchGenRef.current) setProjects(next);
    } catch {
      // Keep the current list.
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (loading || failed || autoSelectedRef.current || projects.length === 0) {
      return;
    }
    autoSelectedRef.current = true;
    if (requestedId === null && projects.length === 1) {
      setFallbackId(projects[0].id);
    }
  }, [loading, failed, projects, requestedId]);

  const activeProject = useMemo(
    () => projects.find(p => p.id === requestedId) ?? null,
    [projects, requestedId],
  );

  const status: ActiveProjectStatus = useMemo(() => {
    if (loading) return 'loading';
    if (failed) return 'error';
    if (projects.length === 0) return 'empty';
    if (requestedId === null) return 'unselected';
    return activeProject ? 'ready' : 'denied';
  }, [loading, failed, projects.length, requestedId, activeProject]);

  const scoped = isProjectScopedPath(pathname);

  // Entering a requirement screen picks up projects created, deleted or
  // re-shared since the list was fetched. The first render is skipped: the
  // initial load above has just fetched it.
  const wasScopedRef = useRef<boolean | null>(null);
  useEffect(() => {
    const wasScoped = wasScopedRef.current;
    wasScopedRef.current = scoped;
    if (wasScoped === null || wasScoped || !scoped) return;
    if (failed) {
      void load();
    } else if (!loading) {
      void refresh();
    }
  }, [scoped, failed, loading, load, refresh]);

  // Remember only a project the user actually has, so a denied id never becomes
  // sticky and cannot follow the user to the next screen.
  useEffect(() => {
    if (status === 'ready' && requestedId !== null) {
      writeStoredProjectId(requestedId);
    }
  }, [status, requestedId]);

  // One-directional: publish a resolved selection into the URL so the screen
  // can be linked and reloaded. Runs only where the id means something, and
  // only when the URL does not already say it — after the write the condition
  // is false, so this settles instead of ping-ponging with the reader above.
  useEffect(() => {
    if (!scoped || status !== 'ready' || requestedId === null) return;
    if (urlProjectId === requestedId) return;

    const params = new URLSearchParams(searchParams);
    params.set(ACTIVE_PROJECT_QUERY_PARAM, String(requestedId));
    // `replace` so switching projects does not stack history entries the back
    // button then has to walk through.
    setSearchParams(params, { replace: true });
  }, [
    scoped,
    status,
    requestedId,
    urlProjectId,
    searchParams,
    setSearchParams,
  ]);

  const selectProject = useCallback(
    (projectId: number | null) => {
      if (projectId === null) {
        setFallbackId(null);
        writeStoredProjectId(null);
        if (searchParams.has(ACTIVE_PROJECT_QUERY_PARAM)) {
          const params = new URLSearchParams(searchParams);
          params.delete(ACTIVE_PROJECT_QUERY_PARAM);
          setSearchParams(params, { replace: true });
        }
        return;
      }
      // Guard here as well as in `status`: a caller should not be able to move
      // the UI onto a project the backend never offered.
      if (!projects.some(p => p.id === projectId)) return;

      setFallbackId(projectId);
      // The URL wins over `fallbackId`, so it has to be updated in the same
      // action — otherwise a stale `?projectId=` would keep overriding the
      // user's choice.
      if (scoped) {
        const params = new URLSearchParams(searchParams);
        params.set(ACTIVE_PROJECT_QUERY_PARAM, String(projectId));
        setSearchParams(params, { replace: true });
      } else if (searchParams.has(ACTIVE_PROJECT_QUERY_PARAM)) {
        const params = new URLSearchParams(searchParams);
        params.delete(ACTIVE_PROJECT_QUERY_PARAM);
        setSearchParams(params, { replace: true });
      }
    },
    [projects, scoped, searchParams, setSearchParams],
  );

  const reload = useCallback(() => {
    void load();
  }, [load]);

  const value = useMemo<ActiveProjectContextValue>(
    () => ({
      status,
      activeProjectId: status === 'ready' ? requestedId : null,
      activeProject: status === 'ready' ? activeProject : null,
      projects,
      selectProject,
      reload,
    }),
    [status, requestedId, activeProject, projects, selectProject, reload],
  );

  return (
    <ActiveProjectContext.Provider value={value}>
      {children}
    </ActiveProjectContext.Provider>
  );
};

/**
 * Read the active project. Throws when used outside the provider — that is a
 * wiring mistake, not a runtime condition worth rendering around.
 */
export function useActiveProject(): ActiveProjectContextValue {
  const ctx = useContext(ActiveProjectContext);
  if (!ctx) {
    throw new Error(
      'useActiveProject must be used within an ActiveProjectProvider',
    );
  }
  return ctx;
}
